<template>
  <div class="sync-element-not-used-templates">
    <div class="description-section">
      <slot name="description" />
    </div>

    <HelpMessage
      class="mb-3"
      :message="t('templateBuilder.saveSyncElementModal.helpMessageTitle')"
      :link="{
        type: 'gleap',
        articleId: '165',
        label: t('showHelpArticle')
      }"
    />

    <div class="reveal-section">
      <Button
        :label="showTemplates ? t('templateBuilder.saveSyncElementModal.hideNotUsedTemplates') : t('templateBuilder.saveSyncElementModal.showNotUsedTemplates')"
        :icon="showTemplates ? 'far fa-eye-slash' : 'far fa-eye'"
        class="p-button-secondary"
        @click="toggleShowTemplates"
      />
    </div>

    <div
      v-if="showTemplates"
      class="templates-section mt-3"
    >
      <!-- Selection Controls -->
      <div class="selection-controls mb-3">
        <div class="selection-info">
          <div class="selection-counter">
            <strong>{{ selectedCount }}</strong> {{ t('templateBuilder.saveSyncElementModal.templatesSelected', { count: selectedCount }) }}
          </div>
          <div class="selection-actions">
            <Button
              v-if="selectedCount > 0"
              :label="t('templateBuilder.saveSyncElementModal.deselectAll')"
              icon="far fa-square"
              class="p-button-text p-button-sm"
              @click="deselectAll"
            />
            <!-- View Switcher -->
            <div class="view-switcher">
              <Button
                v-tooltip="{ value: t('templateBuilder.saveSyncElementModal.gridView') }"
                :class="{ 'active': currentView === 'grid' }"
                icon="far fa-grid"
                class="p-button-text p-button-sm view-button"
                @click="switchView('grid')"
              />
              <Button
                v-tooltip="{ value: t('templateBuilder.saveSyncElementModal.listView') }"
                :class="{ 'active': currentView === 'list' }"
                icon="far fa-list"
                class="p-button-text p-button-sm view-button"
                @click="switchView('list')"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Grid View -->
      <TemplateGridView
        v-if="currentView === 'grid'"
        :list="list"
        :is-list-loading="isListLoading"
        :base-url="baseUrl"
        :id-shop="idShop"
        :image-load-time="imageLoadTime"
        :selected-templates="selectedTemplates"
        :is-element-part-type-section="isElementPartTypeSection"
        :get-selected-sections-for-template="getSelectedSectionsForTemplate"
        @handle-scroll="handleScroll"
        @image-loaded="imageLoaded"
        @image-failed="imageFailed"
        @preview-image="handleGridPreviewImage"
        @toggle-selection="toggleSelection"
      />

      <!-- List View -->
      <TemplateListView
        v-else-if="currentView === 'list'"
        ref="templateListViewRef"
        :id-shop="idShop"
        :sync-element-id="syncElementId"
        :template-type="templateType"
        :element-part-type-or-subtype="elementPartTypeOrSubtype"
        :is-part-type="isPartType"
        :selected-templates="selectedTemplates"
        :selected-sections-by-template="selectedSectionsByTemplate"
        :exclude-ids="excludeIds"
        :version="version"
        :fetched-templates="fetchedTemplates"
        :fetched-workflows="fetchedWorkflows"
        @preview-template="handleListPreviewImage"
        @selection-changed="handleListSelectionChanged"
        @open-section-modal="handleOpenSectionModal"
        @fetched-templates="handleFetchedTemplates"
        @fetched-workflows="handleFetchedWorkflows"
      />
    </div>

    <PreviewModal
      v-model:visible="displayPreviewModal"
      :images="imagesPreview"
      @on-close-preview-modal="handleClosePreview"
    />

    <SectionSelectionModal
      v-model:visible="displaySectionModal"
      :template-name="currentTemplate?.label || ''"
      :template-config="currentTemplate?.template_config || ''"
      :initial-selected-sections="getSelectedSectionsForTemplate(currentTemplate?.id_template || 0)"
      @sections-selected="handleSectionsSelected"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent, ref, Ref, computed, onBeforeUnmount, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import PreviewModal from '@/components/modals/PreviewModal.vue';
import HelpMessage from '@/components/common/HelpMessage.vue';
import SectionSelectionModal from '@/components/modals/SectionSelectionModal.vue';
import Tooltip from 'primevue/tooltip';
import TemplateGridView from '@/components/template-builder/TemplateGridView.vue';
import TemplateListView from '@/components/template-builder/TemplateListView.vue';
import { TemplatesCustom } from '@/types/generated-types/graphql';
import { getTemplatesNotUsedBySyncElement } from '@/composables/template/SyncElement';
import { TemplateStructureEnum } from '@/types';
import { loadImageUntilSuccess } from '@/composables/loadImage';
import { isDisplayTemplate } from '@/components/template-builder/utils/helpers';
import CryptoJS from 'crypto-js';

// Extended interface to include name_marketing_workflow from GraphQL response
interface ExtendedTemplatesCustom extends TemplatesCustom {
  name_campaign_automation?: string;
}

export default defineComponent({
  name: 'SyncElementNotUsedTemplates',

  components: {
    Button,
    PreviewModal,
    SectionSelectionModal,
    HelpMessage,
    TemplateGridView,
    TemplateListView,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idShop: {
      type: Number,
      required: true,
    },

    syncElementId: {
      type: String,
      required: true,
    },

    templateType: {
      type: String,
      required: true,
    },

    elementPartTypeOrSubtype: {
      type: String,
      required: true,
    },

    isPartType: {
      type: Boolean,
      required: true,
    },

    excludeIds: {
      type: Array as PropType<number[]>,
      default: () => [],
    },

    version: {
      type: Number,
      default: undefined,
    },
  },

  emits: {
    loaded: (total: number) => true,
    'selection-changed': (data: { selectedIds: number[]; selectedSectionsByTemplate: Record<number, string[]> }) => true,
  },

  expose: ['resetSelection', 'fetchedWorkflows'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const baseUrl = `${process.env.VUE_APP_URL_MEDIAL_IMAGE_PREFIX_WITH_RESIZE}`;

    const list: Ref<ExtendedTemplatesCustom[]> = ref([]);
    const isListLoading = ref(false);
    const imageLoadTime = ref<Record<number, number>>({});
    const imagesLoaded = ref<Record<number, boolean>>({});
    const imageRetryCount = ref<Record<number, number>>({});
    const displayPreviewModal = ref(false);
    const imagesPreview = ref([{}]);
    const total = ref(0);
    const lastTemplateId = ref<number | undefined>(undefined);
    const limit = 12;
    const scrollContainer = ref<HTMLElement>();
    const isLoadingMore = ref(false);
    const selectedTemplates = ref<Set<number>>(new Set());
    const showTemplates = ref(false);
    const templateListViewRef = ref<InstanceType<typeof TemplateListView> | null>(null);

    // View management state
    const currentView = ref<'grid' | 'list'>('list');

    // Section selection modal state
    const displaySectionModal = ref(false);
    const currentTemplate = ref<ExtendedTemplatesCustom | null>(null);
    const selectedSectionsByTemplate = ref<Map<number, string[]>>(new Map());

    const hasMore = computed(() => list.value.length < total.value);
    const selectedCount = computed(() => selectedTemplates.value.size);

    const loadTemplates = async (loadMore = false) => {
      if (!loadMore) {
        lastTemplateId.value = undefined;
        list.value = [];
      }

      if (loadMore && isLoadingMore.value) {
        return; // Prevent multiple simultaneous loading
      }

      isListLoading.value = true;
      if (loadMore) {
        isLoadingMore.value = true;
      }

      try {
        const result = await getTemplatesNotUsedBySyncElement({
          idShop: props.idShop,
          syncElementId: props.syncElementId,
          templateType: props.templateType,
          elementPartTypeOrSubtype: props.elementPartTypeOrSubtype,
          limit,
          lastTemplateId: lastTemplateId.value,
          isPartType: props.isPartType,
          excludeIds: props.excludeIds,
          version: props.version,
        });

        if (result) {
          const newItems = result.items as TemplatesCustom[];

          if (loadMore) {
            list.value = [...list.value, ...newItems];
          } else {
            list.value = newItems;
          }
          total.value = result.total;

          // Update lastTemplateId with the last item's id for cursor pagination
          if (newItems.length > 0) {
            lastTemplateId.value = newItems[newItems.length - 1].id_template;
          }

          // Emit loaded event with total count
          emit('loaded', result.total);
        }
      } catch (error) {
        console.error('Error loading templates not used by sync element:', error);
      } finally {
        isListLoading.value = false;
        if (loadMore) {
          isLoadingMore.value = false;
        }
      }
    };

    const handleScroll = async (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop } = target;
      const { scrollHeight } = target;
      const { clientHeight } = target;

      // Check if we're near the bottom (within 100px)
      if (scrollTop + clientHeight >= scrollHeight - 100 && hasMore.value && !isListLoading.value && !isLoadingMore.value) {
        await loadTemplates(true);
      }
    };

    const imageLoaded = (id_template: number) => {
      if (!imagesLoaded.value[id_template]) {
        imagesLoaded.value[id_template] = true;
        imageLoadTime.value[id_template] = new Date().getTime();
      }
    };

    const imageFailed = (id_template: number) => {
      if (!imageRetryCount.value[id_template]) {
        imageRetryCount.value[id_template] = 0;
      }

      if (imageRetryCount.value[id_template] >= 3) {
        return;
      }

      imageRetryCount.value[id_template] += 1;

      if (!imagesLoaded.value[id_template]) {
        setTimeout(() => {
          imageLoadTime.value[id_template] = new Date().getTime();
        }, 3000);
      }
    };

    const handlePreviewImage = async (idTemplate: number, templateType: string, templateLabel: string) => {
      imagesPreview.value = [];

      // Generate crypted key like in MyTemplates.vue
      const cryptedImageKey = CryptoJS.SHA1(`salt-${idTemplate}`).toString();
      const potentialImageUrl = `${baseUrl}${props.idShop}_${cryptedImageKey}${isDisplayTemplate(templateType) ? '-isdisplay' : ''}.png&w=400&${new Date().getTime()}`;

      try {
        await loadImageUntilSuccess(potentialImageUrl);
        imagesPreview.value.push({
          itemImageSrc: potentialImageUrl,
          thumbnailImageSrc: potentialImageUrl,
          title: templateLabel,
        });
        displayPreviewModal.value = true;
      } catch (error) {
        console.error("Erreur lors du chargement de l'image", error);
      }
    };

    // Handler for grid view with callback support
    const handleGridPreviewImage = async (data: { id: number; templateType: string; templateLabel: string }, callback?: () => void) => {
      try {
        await handlePreviewImage(data.id, data.templateType, data.templateLabel);
      } finally {
        if (callback) callback();
      }
    };

    // Handler for list view with callback support
    const handleListPreviewImage = async (data: { id: number; templateType: string; templateLabel: string }, callback?: () => void) => {
      try {
        await handlePreviewImage(data.id, data.templateType, data.templateLabel);
      } finally {
        if (callback) callback();
      }
    };

    const handleClosePreview = () => {
      displayPreviewModal.value = false;
    };

    // Check if element part type is a section
    const isElementPartTypeSection = computed(() => props.elementPartTypeOrSubtype === TemplateStructureEnum.SECTION);

    const getSelectedSectionsForTemplate = (templateId: number): string[] => selectedSectionsByTemplate.value.get(templateId) || [];

    const handleSectionsSelected = (selectedSections: string[]) => {
      if (currentTemplate.value) {
        selectedSectionsByTemplate.value.set(currentTemplate.value.id_template, selectedSections);

        // If sections are selected, mark the template as selected
        if (selectedSections.length > 0) {
          selectedTemplates.value.add(currentTemplate.value.id_template);
        } else {
          // If no sections selected, remove template from selection
          selectedTemplates.value.delete(currentTemplate.value.id_template);
        }

        // Synchronize with TemplateListView if it's the current view
        if (currentView.value === 'list' && templateListViewRef.value) {
          templateListViewRef.value.handleSectionsSelected(currentTemplate.value.id_template, selectedSections);
        }

        emit('selection-changed', {
          selectedIds: Array.from(selectedTemplates.value),
          selectedSectionsByTemplate: Object.fromEntries(selectedSectionsByTemplate.value),
        });
      }
    };

    const toggleSelection = (templateId: number) => {
      // If element part type is a section, open section selection modal
      if (isElementPartTypeSection.value) {
        const template = list.value.find((item: any) => item.id_template === templateId);
        if (template) {
          currentTemplate.value = template;
          displaySectionModal.value = true;
        }
        return;
      }

      // Default behavior for non-section elements
      if (selectedTemplates.value.has(templateId)) {
        selectedTemplates.value.delete(templateId);
      } else {
        selectedTemplates.value.add(templateId);
      }

      // Emit the updated selection
      emit('selection-changed', {
        selectedIds: Array.from(selectedTemplates.value),
        selectedSectionsByTemplate: Object.fromEntries(selectedSectionsByTemplate.value),
      });
    };

    const deselectAll = () => {
      selectedTemplates.value.clear();
      selectedSectionsByTemplate.value.clear();
      emit('selection-changed', {
        selectedIds: [],
        selectedSectionsByTemplate: {},
      });
    };

    const toggleShowTemplates = async () => {
      showTemplates.value = !showTemplates.value;

      // Load templates when showing for the first time
      if (showTemplates.value && list.value.length === 0 && currentView.value === 'grid') {
        await loadTemplates();
      }
    };

    const switchView = (view: 'grid' | 'list') => {
      currentView.value = view;
      if (currentView.value === 'grid' && list.value.length === 0) {
        loadTemplates();
      }
    };

    const handleListSelectionChanged = (data: { selectedIds: number[]; selectedSectionsByTemplate: Record<number, string[]> }) => {
      // Update the selected templates based on the list view selection
      selectedTemplates.value.clear();
      data.selectedIds.forEach((id) => selectedTemplates.value.add(id));

      // Update selectedSectionsByTemplate map
      selectedSectionsByTemplate.value.clear();
      Object.entries(data.selectedSectionsByTemplate).forEach(([templateId, sections]) => {
        selectedSectionsByTemplate.value.set(Number(templateId), sections);
      });

      // Emit the updated selection
      emit('selection-changed', {
        selectedIds: Array.from(selectedTemplates.value),
        selectedSectionsByTemplate: Object.fromEntries(selectedSectionsByTemplate.value),
      });
    };

    const handleOpenSectionModal = (template: ExtendedTemplatesCustom) => {
      currentTemplate.value = template;
      displaySectionModal.value = true;
    };

    const fetchedTemplates = ref<any[]>([]);
    const fetchedWorkflows = ref<any[]>([]);

    const handleFetchedTemplates = (templates: any[]) => {
      fetchedTemplates.value = templates;
    };

    const handleFetchedWorkflows = (workflows: any[]) => {
      fetchedWorkflows.value = workflows;
    };

    onBeforeUnmount(() => {
      if (scrollContainer.value) {
        scrollContainer.value.removeEventListener('scroll', handleScroll);
      }
    });

    return {
      t,
      list,
      isListLoading,
      baseUrl,
      imageLoadTime,
      displayPreviewModal,
      imagesPreview,
      selectedCount,
      selectedTemplates,
      showTemplates,
      currentView,
      displaySectionModal,
      currentTemplate,
      isElementPartTypeSection,
      templateListViewRef,
      selectedSectionsByTemplate,
      fetchedTemplates,
      fetchedWorkflows,
      handleScroll,
      imageLoaded,
      imageFailed,
      handleGridPreviewImage,
      handleListPreviewImage,
      handleClosePreview,
      handleFetchedTemplates,
      handleFetchedWorkflows,
      toggleSelection,
      deselectAll,
      toggleShowTemplates,
      switchView,
      handleListSelectionChanged,
      getSelectedSectionsForTemplate,
      handleSectionsSelected,
      handleOpenSectionModal,
    };
  },
});
</script>

<style scoped lang="scss">
$img-size: 12rem;

.sync-element-not-used-templates {
  .description-section {
    text-align: left;
    margin-bottom: 1rem;
  }

  .reveal-section {
    text-align: left;
  }

  .total-templates-hint {
    color: #6c757d;
    font-size: 0.9rem;

    i {
      margin-right: 0.5rem;
    }
  }

  .templates-section {
    .selection-controls {
      background: #f8f9fa;
      border: 1px solid #e9ecef;
      border-radius: 8px;
      padding: 1rem;

      .selection-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;

        .selection-counter {
          font-size: 1rem;
          color: #495057;

          strong {
            color: #2c3e50;
            font-size: 1.1rem;
          }
        }

        .selection-actions {
          display: flex;
          gap: 0.5rem;
          align-items: center;

          .view-switcher {
            display: flex;
            border: 1px solid #e9ecef;
            border-radius: 6px;
            overflow: hidden;
            margin-right: 0.5rem;

            .view-button {
              border: none !important;
              border-radius: 0 !important;
              padding: 0.5rem 0.75rem !important;
              transition: all 0.2s ease;

              &:hover {
                background: #f8f9fa !important;
              }

              &.active {
                background: #94c840 !important;
                color: white !important;
              }

              &:first-child {
                border-right: 1px solid #e9ecef;
              }
            }
          }
        }
      }
    }

    .loader-container {
      text-align: center;
      padding: 3rem;
    }

    .empty-message {
      text-align: center;
      padding: 3rem;

      i {
        display: block;
        margin-bottom: 1rem;
      }

      p {
        margin: 0;
        font-size: 1.1rem;
      }
    }
  }
}

// Responsive design
@media (max-width: 768px) {
  .sync-element-not-used-templates {
    .templates-section {
      .selection-controls {
        .selection-info {
          flex-direction: column;
          align-items: flex-start;
          gap: 0.75rem;

          .selection-actions {
            align-self: stretch;
            justify-content: center;
          }
        }
      }
    }
  }
}
</style>
